import React from "react";
import Popover from "./Popover";
import { useSources } from "../hooks/useBdApi";
import { relativeTime } from "../metrics";

// External-link glyph for report links.
function ExternalIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 3h7v7M21 3l-9 9M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </svg>
  );
}

/**
 * "Where does this come from?" marker.
 *
 * Renders a small "i" icon. Hovering / focusing shows a popover describing how
 * the number is derived and listing the originating Salesforce report(s) as
 * clickable links. Clicking the icon pins the popover open (so the links can
 * be clicked); clicking again, pressing Escape or clicking outside closes it
 * (see <Popover>).
 *
 * Resolve sources either by `metric` (a key in meta.sources.metrics, e.g.
 * "meetingsHappened") or by passing `reports` (array of report keys) plus
 * `how` / `attribution` text directly. `title` overrides the heading.
 */
export default function SourceTip({ metric, reports, how, attribution, title, hint, className = "" }) {
  const { sources, sync } = useSources();

  const def = metric && sources && sources.metrics ? sources.metrics[metric] : null;
  const reportKeys = reports || (def ? def.reports : []) || [];
  const reportDefs = reportKeys.map((k) => (sources && sources.reports ? sources.reports[k] : null)).filter(Boolean);
  const howText = how || (def ? def.how : "") || hint || "";
  const attrText = attribution !== undefined ? attribution : def ? def.attribution : "";
  const heading = title || (def ? def.label : "") || "Data source";

  if (!howText && !reportDefs.length) return null;

  const lastSync = sync && sync.lastSuccess ? sync.lastSuccess.finishedAt || sync.lastSuccess.startedAt : null;
  const hasReport = reportDefs.some((r) => r.isReport !== false);

  return (
    <Popover
      className={`bd-source-tip ${className}`}
      popClassName="bd-source-tip__pop"
      label={`Data source for ${heading}`}
      trigger={(props) => (
        <button type="button" className="bd-source-tip__btn" aria-label={`Data source: ${heading}`} {...props}>
          i
        </button>
      )}
    >
      {({ pinned }) => (
        <>
          <div className="bd-source-tip__title">
            <span>{heading}</span>
            <span className="bd-source-tip__kicker">{hasReport ? "Salesforce report" : "Salesforce"}</span>
          </div>
          {howText && <p className="bd-source-tip__how">{howText}</p>}
          {reportDefs.length > 0 && (
            <ul className="bd-source-tip__sources">
              {reportDefs.map((r) => (
                <li key={r.key}>
                  <a className="bd-source-tip__source" href={r.url} target="_blank" rel="noopener noreferrer" title={r.filterText}>
                    <span className="bd-source-tip__source-name">
                      {r.name}
                      <ExternalIcon />
                    </span>
                    <span className="bd-source-tip__source-meta">
                      {r.objectLabel}
                      {r.id ? ` · ${r.id}` : ""}
                    </span>
                    {r.filterText && <span className="bd-source-tip__source-meta">{r.filterText}</span>}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {attrText && <p className="bd-source-tip__attr">{attrText}</p>}
          <div className="bd-source-tip__foot">
            <span>{lastSync ? `Synced ${relativeTime(lastSync)}` : "Synced daily 7:00 AM ET"}</span>
            <span>{pinned ? "Click i to close" : "Click i to pin"}</span>
          </div>
        </>
      )}
    </Popover>
  );
}

/**
 * Wraps a card / section title with a SourceTip for `metric`.
 */
export function TitleWithSource({ children, metric, reports, how, attribution, title }) {
  return (
    <span className="bd-title-with-source">
      {children}
      <SourceTip metric={metric} reports={reports} how={how} attribution={attribution} title={title || (typeof children === "string" ? children : undefined)} />
    </span>
  );
}
